import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Trash2, KeyRound, Save, X } from 'lucide-react';
import StatusBoard from '../../components/StatusBoard';

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miercoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sabado' },
  { key: 'sunday', label: 'Domingo' },
];

const emptySchedule = () => DAYS.reduce((acc, d) => {
  acc[d.key] = { enabled: false, start: '09:00', end: '18:00', break_start: '', break_end: '' };
  return acc;
}, {});

const emptyForm = () => ({
  name: '', pin: '', hourly_wage: '',
  vacation_days: '0', schedule: emptySchedule(),
  penalty_mode: '', penalty_value: ''
});

export default function Employees() {
  const { getAuthHeaders } = useAuth();
  const [list, setList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPin, setResetPin] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/timeclock/employees`, { headers: getAuthHeaders() });
      setList(r.data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
    setError('');
  };

  const startEdit = (emp) => {
    setEditingId(emp.id);
    setForm({
      name: emp.name || '',
      pin: '',
      hourly_wage: String(emp.hourly_wage ?? ''),
      vacation_days: String(emp.vacation_days ?? '0'),
      schedule: { ...emptySchedule(), ...(emp.schedule || {}) },
      penalty_mode: emp.penalty_mode || '',
      penalty_value: emp.penalty_value != null ? String(emp.penalty_value) : '',
    });
    setShowForm(true);
    setError('');
  };

  const save = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      name: form.name,
      hourly_wage: parseFloat(form.hourly_wage) || 0,
      vacation_days: parseFloat(form.vacation_days) || 0,
      schedule: form.schedule,
      penalty_mode: form.penalty_mode || null,
      penalty_value: form.penalty_value !== '' ? parseFloat(form.penalty_value) : null,
    };
    if (form.pin) payload.pin = form.pin;
    try {
      if (editingId) {
        await axios.put(`${API}/api/timeclock/employees/${editingId}`, payload, { headers: getAuthHeaders() });
      } else {
        if (!form.pin) { setError('PIN requerido'); return; }
        await axios.post(`${API}/api/timeclock/employees`, payload, { headers: getAuthHeaders() });
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar empleado? Sus registros se conservan.')) return;
    await axios.delete(`${API}/api/timeclock/employees/${id}`, { headers: getAuthHeaders() });
    load();
  };

  const submitResetPin = async () => {
    setError('');
    try {
      await axios.post(`${API}/api/timeclock/employees/${resetTarget.id}/reset-pin`,
        { pin: resetPin }, { headers: getAuthHeaders() });
      setResetTarget(null);
      setResetPin('');
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Error'); }
  };

  const updateDay = (key, patch) => {
    setForm(f => ({ ...f, schedule: { ...f.schedule, [key]: { ...f.schedule[key], ...patch } } }));
  };

  return (
    <div className="space-y-4" data-testid="tc-employees-section">
      {!showForm && !resetTarget && (
        <>
          <StatusBoard variant="admin" pollMs={10000} />
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-semibold text-slate-700">Empleados ({list.length})</h2>
            <Button onClick={startCreate} className="bg-[#1D3C6E] hover:bg-[#152B50] h-9" data-testid="tc-add-employee-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuevo Empleado
            </Button>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Nombre</th>
                  <th className="text-left px-4 py-3">PIN</th>
                  <th className="text-right px-4 py-3">Sueldo/hr</th>
                  <th className="text-right px-4 py-3">Vac. dias</th>
                  <th className="text-center px-4 py-3">Estatus</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-400 text-xs">Sin empleados</td></tr>
                ) : list.map(emp => (
                  <tr key={emp.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{emp.name}</td>
                    <td className="px-4 py-3 font-mono">••••</td>
                    <td className="px-4 py-3 text-right">${(emp.hourly_wage || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">{emp.vacation_days}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`status-badge ${emp.active === false ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-700'}`}>
                        {emp.active === false ? 'Inactivo' : 'Activo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => startEdit(emp)} className="text-xs text-[#1D3C6E] hover:underline mr-3" data-testid={`tc-edit-emp-${emp.id}`}>Editar</button>
                      <button onClick={() => { setResetTarget(emp); setResetPin(''); }} className="text-xs text-slate-500 hover:text-slate-700 mr-3" title="Resetear PIN">
                        <KeyRound className="inline w-3 h-3" /> PIN
                      </button>
                      <button onClick={() => remove(emp.id)} className="text-xs text-red-500 hover:text-red-700">
                        <Trash2 className="inline w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Reset PIN Form */}
      {resetTarget && (
        <div className="bg-white rounded-lg border border-slate-200 p-5 max-w-sm">
          <h3 className="text-sm font-semibold mb-3">Resetear PIN — {resetTarget.name}</h3>
          <Input type="text" inputMode="numeric" maxLength={4}
            placeholder="Nuevo PIN (4 digitos)"
            value={resetPin}
            onChange={(e) => setResetPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            data-testid="tc-reset-pin-input" />
          {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
          <div className="flex gap-2 mt-3">
            <Button onClick={submitResetPin} disabled={resetPin.length !== 4} className="bg-[#1D3C6E]" data-testid="tc-reset-pin-confirm">Confirmar</Button>
            <Button variant="outline" onClick={() => { setResetTarget(null); setError(''); }}>Cancelar</Button>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <form onSubmit={save} className="bg-white rounded-lg border border-slate-200 p-5 space-y-4" data-testid="tc-employee-form">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold">{editingId ? 'Editar' : 'Nuevo'} Empleado</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Nombre *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="tc-emp-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">PIN (4 digitos) {editingId && <span className="text-slate-400">(dejar vacio para no cambiar)</span>}</label>
              <Input type="text" inputMode="numeric" maxLength={4}
                value={form.pin}
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                placeholder="0000"
                data-testid="tc-emp-pin" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Sueldo por hora *</label>
              <Input type="number" step="0.01" value={form.hourly_wage}
                onChange={(e) => setForm({ ...form, hourly_wage: e.target.value })} required data-testid="tc-emp-wage" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Dias de vacaciones disponibles</label>
              <Input type="number" step="0.5" value={form.vacation_days}
                onChange={(e) => setForm({ ...form, vacation_days: e.target.value })} data-testid="tc-emp-vac" />
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-700 mb-2">Horario semanal</h4>
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              {DAYS.map(d => {
                const day = form.schedule[d.key] || { enabled: false };
                return (
                  <div key={d.key} className="grid grid-cols-12 gap-2 items-center px-3 py-2">
                    <label className="col-span-2 flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!day.enabled}
                        onChange={(e) => updateDay(d.key, { enabled: e.target.checked })}
                        data-testid={`tc-day-${d.key}`} />
                      {d.label}
                    </label>
                    {day.enabled ? (
                      <>
                        <div className="col-span-2"><Input className="h-8 text-sm" type="time" value={day.start || ''} onChange={(e) => updateDay(d.key, { start: e.target.value })} /></div>
                        <div className="col-span-2"><Input className="h-8 text-sm" type="time" value={day.end || ''} onChange={(e) => updateDay(d.key, { end: e.target.value })} /></div>
                        <div className="col-span-3 flex items-center gap-1">
                          <span className="text-xs text-slate-400">Receso:</span>
                          <Input className="h-8 text-sm" type="time" value={day.break_start || ''} onChange={(e) => updateDay(d.key, { break_start: e.target.value })} />
                        </div>
                        <div className="col-span-3"><Input className="h-8 text-sm" type="time" value={day.break_end || ''} onChange={(e) => updateDay(d.key, { break_end: e.target.value })} /></div>
                      </>
                    ) : <span className="col-span-10 text-xs text-slate-400">Descanso</span>}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Penalizacion por auto-cierre (sobreescribe global)</label>
              <select className="w-full h-9 px-2 border border-slate-200 rounded-md text-sm"
                value={form.penalty_mode}
                onChange={(e) => setForm({ ...form, penalty_mode: e.target.value })}>
                <option value="">— Usar configuracion global —</option>
                <option value="fixed">Monto fijo ($)</option>
                <option value="minutes">Minutos descontados</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Valor</label>
              <Input type="number" step="0.01" value={form.penalty_value}
                onChange={(e) => setForm({ ...form, penalty_value: e.target.value })}
                disabled={!form.penalty_mode} placeholder="—" />
            </div>
          </div>

          {error && <p className="text-sm text-red-500" data-testid="tc-emp-error">{error}</p>}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="tc-emp-save"><Save className="w-4 h-4 mr-1" /> Guardar</Button>
          </div>
        </form>
      )}
    </div>
  );
}
